import { useState } from 'react'
import dayjs from 'dayjs'
import { useSnackbar } from 'notistack'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useSetRecoilState } from 'recoil'
import styled from 'styled-components'

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'

import { useNicknameDuplicateCheck } from '~/features/auth/queries/useAuth'
import { useMyInfo, usePatchMyInfo } from '~/features/mypage/queries/useMyInfo'
import Close from '~/shared/assets/icons/Close'
import Button from '~/shared/components/buttons/Button'
import Dropdown from '~/shared/components/dropdown/Dropdown'
import LoadingScreen from '~/shared/components/loading/LoadingScreen'
import { isModalOpenState } from '~/shared/states'
import theme from '~/shared/styles/theme'

type Inputs = {
  nickname: string
  name: string
  gender: string
  birthYear: string | number
  email: string
}

function ModifyInfoModal() {
  const setIsModalOpen = useSetRecoilState(isModalOpenState)

  const { enqueueSnackbar } = useSnackbar()

  const [nicknameValidatedMsg, setNicknameValidatedMsg] = useState<string>('')

  const { data, isLoading } = useMyInfo()

  const { userInfo } = data! || {}

  const { mutate: patchMyInfo } = usePatchMyInfo()
  const { mutate: nicknameDuplicateCheck } = useNicknameDuplicateCheck()

  if (isLoading) return <LoadingScreen />

  const { register, handleSubmit, watch, setValue } = useForm<Inputs>({
    defaultValues: {
      nickname: userInfo.nickname,
      name: userInfo.name,
      gender: userInfo.gender,
      birthYear: userInfo.year_of_birth.slice(0, 4),
      email: userInfo.email,
    },
  })

  const [nickname, name, gender, birthYear] = watch([
    'nickname',
    'name',
    'gender',
    'birthYear',
  ])

  const checkNicknameValidated = (nicknameInput: string) => {
    nicknameDuplicateCheck(nicknameInput, {
      onSuccess: (response) => {
        if (response.errorCode === -2003) {
          setNicknameValidatedMsg(response.errorMsg)
        } else {
          setNicknameValidatedMsg('')
        }
      },
      onError: () => {
        enqueueSnackbar('에러가 발생했습니다. 다시 시도해주세요.', {
          variant: 'error',
        })
      },
    })
  }

  const changeNicknameInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      setNicknameValidatedMsg('닉네임 중복 체크가 필요합니다.')
      setValue('nickname', e.target.value)
    }
  }

  const onSubmit: SubmitHandler<Inputs> = () => {
    patchMyInfo(
      {
        userInfo: {
          name: name,
          nickname: nickname,
          year_of_birth: birthYear.toString(),
          gender: gender,
        },
      },
      {
        onSuccess: () => {
          enqueueSnackbar('정보가 수정되었습니다.', { variant: 'success' })
          setIsModalOpen({ contents: '', isOpen: false })
          localStorage.setItem('starlike-user-name', name)
          window.location.reload()
        },
      },
    )
  }

  return (
    <Wrapper onClick={(e) => e.stopPropagation()}>
      <CloseIcon
        onClick={() => setIsModalOpen({ contents: '', isOpen: false })}
      />
      <Title>정보 수정</Title>
      <InputBox onSubmit={handleSubmit(onSubmit)}>
        <div>
          <InputTitle>닉네임</InputTitle>
          <Input
            className={`${nicknameValidatedMsg && 'warning'}`}
            type="text"
            placeholder="홍길동"
            {...register('nickname')}
            onChange={changeNicknameInput}
          />
          {nicknameValidatedMsg && (
            <WarningText>*{nicknameValidatedMsg}</WarningText>
          )}
        </div>

        <Button
          $width={141}
          $height={58}
          $fontSize={18}
          $variant="white"
          type="button"
          onClick={() => checkNicknameValidated(nickname)}
        >
          {nicknameValidatedMsg ? '중복 확인' : '확인됨'}
        </Button>

        <div>
          <InputTitle>이름</InputTitle>
          <Input type="text" placeholder={name} {...register('name')} />
        </div>
        <div>
          <InputTitle>성별</InputTitle>
          <GenderDropdown
            options={[
              { value: 'M', label: '남성' },
              { value: 'F', label: '여성' },
            ]}
            {...register('gender')}
          />
        </div>
        <div>
          <InputTitle>출생년도</InputTitle>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <StyledDatePicker
              views={['year']}
              defaultValue={dayjs(userInfo.year_of_birth)}
              onChange={(e) => {
                if (e) {
                  setValue('birthYear', e.year().toString())
                }
              }}
            />
          </LocalizationProvider>
        </div>
        <div>
          <InputTitle>이메일</InputTitle>
          <Input type="text" placeholder={userInfo.email} disabled />
        </div>
        <ModifyButton type="submit" value="저장하기" />
      </InputBox>
    </Wrapper>
  )
}

export default ModifyInfoModal

const Wrapper = styled.section`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  overflow-y: scroll;
  display: flex;
  flex-direction: column;
  gap: 36px;
  align-items: center;

  box-sizing: border-box;
  width: 500px;
  height: auto;
  max-height: 80vh;
  margin: 0 auto;
  padding: 108px 77px 60px;

  background-color: ${theme.colors.white};
  border-radius: 12px;

  @media ${theme.device.medium} {
    top: 50%;
    width: 90%;
    height: auto;
    padding: 56px 20px 30px;
  }
`

const CloseIcon = styled(Close)`
  position: absolute;
  top: 36px;
  right: 36px;

  @media ${theme.device.medium} {
    top: 20px;
    right: 20px;
    width: 24px;
    height: 24px;
  }
`

const Title = styled.h1`
  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 42px;
  font-weight: 500;

  @media ${theme.device.medium} {
    font-size: 36px;
  }
`

const InputBox = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`

const InputTitle = styled.div`
  bottom: 12px;

  margin: 12px 0;

  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 12px;
  color: ${theme.colors.grey_4};
`

const GenderDropdown = styled(Dropdown)`
  width: 100%;
  height: 49px;
`

const StyledDatePicker = styled(DatePicker)`
  width: 100%;
  background-color: ${theme.colors.grey_1};
  border-radius: 12px;

  /* stylelint-disable-next-line selector-class-pattern */
  & .MuiOutlinedInput-root {
    border: none;
  }

  /* stylelint-disable-next-line selector-class-pattern */
  & .MuiOutlinedInput-notchedOutline {
    border: none;
  }
`

const Input = styled.input`
  box-sizing: border-box;
  width: 100%;
  height: 49px;
  padding: 12px 20px;

  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 18px;

  background-color: ${theme.colors.grey_1};
  border: none;
  border-radius: 12px;

  &.warning {
    border: 1px solid ${theme.colors.warning};
  }

  @media ${theme.device.medium} {
    font-size: 15px;
  }
`

const WarningText = styled.div`
  margin: 10px 5px;

  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: ${theme.colors.warning};
`

const ModifyButton = styled.input`
  cursor: pointer;

  gap: 4px;

  width: 100%;
  height: 48px;
  margin-top: 12px;
  padding: 8px 20px;

  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 18px;
  font-weight: 400;
  color: ${theme.colors.white};

  background-color: ${theme.colors.grey_5};
  border: none;
  border-radius: 50px;

  @media ${theme.device.medium} {
    font-size: 15px;
  }
`
